import { RemoveDownloadInProgress } from '~/constants/actions';
import { SetCurrentDocumentsDialog, SetInTranslationProcess } from '~/constants/mutations';
import { CURRENT_DIALOG } from '~/constants/conversationModels';
import { SetSelectedReferencePanel, ShowPreviewDocument, UpdateCurrentLanguageCode } from '~/constants/actions';
import { apiGatewayEndpoints } from '@/services/arcane-api-gateway/api-gateway-endpoints';

export default (ctx, provides) => {
  const { store } = provides;

  const notificationsTypes = [
    {
      notificationType: 'DOCUMENT_TRANSLATION_PENDING',
      transform: (notificationId, notification, createdAt, readAt) => ({
        id: notificationId,
        readAt,
        description: `Getting ready to translate '${notification.name}' (${notification.targetLanguage})`,
        iconName: 'spin',
        createdAt,
      }),
    },
    {
      notificationType: 'DOCUMENT_TRANSLATION_QUEUED',
      transform: (notificationId, notification, createdAt, readAt) => ({
        id: notificationId,
        readAt,
        description: `'${notification.name}' (${notification.targetLanguage}) is queued for translation`,
        iconName: 'spin',
        createdAt,
      }),
    },
    {
      notificationType: 'DOCUMENT_TRANSLATION_TRANSLATING',
      transform: (notificationId, notification, createdAt, readAt) => ({
        id: notificationId,
        readAt,
        description: `'${notification.name}' (${notification.targetLanguage}) is being translated...`,
        iconName: 'spin',
        createdAt,
      }),
    },
    {
      notificationType: 'DOCUMENT_TRANSLATION_DONE',
      transform: (notificationId, notification, createdAt, readAt) => ({
        id: notificationId,
        readAt,
        description: `Downloading translated version of '${notification.name}' (${notification.targetLanguage})`,
        iconName: 'spin',
        createdAt,
      }),
    },
    {
      notificationType: 'DOCUMENT_TRANSLATION_DOWNLOADED',
      transform: (notificationId, notification, createdAt, readAt) => {
        const externalId = String(notification?.externalId || '');
        const isEmailMjml = /(?:^|\/)email\/[^/]+\/content\.mjml$/i.test(externalId);

        if (isEmailMjml && !readAt) {
          store.dispatch(`modalStore/${SetCurrentDocumentsDialog}`, CURRENT_DIALOG.notifications_panel);
          store.dispatch(`documentStore/${SetInTranslationProcess}`, true);
        }

        return {
          id: notificationId,
          readAt,
          description: `'${notification.name}' (${notification.targetLanguage}) is ready!`,
          iconName: 'approved',
          iconType: 'filled',
          ...(isEmailMjml
            ? {
              actionName: 'Reload',
              action: async () => {
                window.location.reload();
              },
            }
            : {
              actionName: 'Download',
              action: async () => {
                const { data } = await provides.arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.pdfUrl, {
                  documentId: notification.externalId,
                  language: notification.targetLanguage,
                });
                window.open(data.url || '');
              },
              secondActionName: 'Preview',
              secondAction: async () => {
                store.dispatch(`documentStore/${SetSelectedReferencePanel}`, {
                  ...notification,
                  type: 'documentTranslation',
                });
                store.dispatch(`translationStore/${UpdateCurrentLanguageCode}`, notification.targetLanguage);
                store.dispatch(`modalStore/${ShowPreviewDocument}`, 1);
              },
            }),
          createdAt,
        };
      },
    },
    {
      notificationType: 'DOCUMENT_TRANSLATION_ERROR',
      transform: (notificationId, notification, createdAt, readAt) => ({
        id: notificationId,
        readAt,
        description: `There was a problem while translating '${notification.name}' (${notification.targetLanguage})`,
        iconName: 'information',
        iconType: 'filled',
        actionName: 'Try again',
        action: async () => {
          await provides.arcaneApiGateway.post(apiGatewayEndpoints.translation.translateDocument, {
            externalId: notification.externalId,
            targetLanguage: notification.targetLanguage,
            languageCode: notification.sourceLanguage,
            name: notification.name,
            conversationId: notification.conversationId,
          });
        },
        createdAt,
      }),
    },
    {
      notificationType: 'EMAIL_ARCHIVE_READY',
      transform: (notificationId, notification, createdAt, readAt) => {
        // Remove download in progress when archive is ready
        store.dispatch(`documentStore/${RemoveDownloadInProgress}`, notification?.emailId);

        return {
          id: notificationId,
          readAt,
          description: `Email archive for '${notification.name}' is ready for download`,
          iconName: 'approved',
          iconType: 'filled',
          actionName: 'Download',
          downloadId: notification?.emailId,
          action: async () => {
            const { filename, downloadRequestId, emailId } = notification;
            const { data: url } = await provides.arcaneApiGateway.get(
              apiGatewayEndpoints.authoredDocuments.authoredDocumentDownloadLink(emailId, downloadRequestId, filename),
            );
            window.open(url, '_blank');
          },
          createdAt,
        };
      },
    },
    {
      notificationType: 'EMAIL_ARCHIVE_ERROR',
      transform: (notificationId, notification, createdAt, readAt) => ({
        id: notificationId,
        readAt,
        description: `There was a problem creating the archive for '${notification.name}'`,
        iconName: 'information',
        iconType: 'filled',
        actionName: 'Try again',
        action: async () => {
          store.dispatch('documentStore/removeDownloadInProgress', notification.emailId);

          await provides.arcaneApiGateway.get({ url: `/authored-content/download/${notification.emailId}` });
        },
        createdAt,
      }),
    },
  ];

  return { notificationsTypes };
};
